import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import authService from '../services/auth';
import { useNavigate } from 'react-router-dom';
import {DiscordUser} from "../types/simc";
import {useEffect} from "react";

export const useAuthStatus = () => {
    const { user, isAuthenticated, isLoading, error } = useAuthStore();
    return {
        user,
        isAuthenticated,
        isLoading,
        error
    };
}

export const useAuth = () => {
    const { user, isAuthenticated, isLoading, setUser, setLoading, setError, error, logout } = useAuthStore();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const { data, error: loginError, isError, isSuccess, isLoading: loginLoading } = useQuery<DiscordUser>({
        queryKey: ['auth'],
        queryFn: () => {
            console.log('fetching auth status');
            setLoading(true);
            return authService.login();
        },
        enabled: isAuthenticated,
        retry: false
    });

    useEffect(() => {
        if (!loginLoading && isError) {
            console.log(loginError);
            setLoading(false);
            logout();
            navigate('/');
        }

        if (isLoading && isSuccess) {
            setLoading(false);
            setUser(data);
            navigate('/dashboard');
        }
    }, [loginError, setUser, loginLoading, isError, isSuccess, navigate, logout, setLoading, isLoading, data]);

    const loginMutation = useMutation({
        mutationFn: async (code: string) => {
            if (isLoading) {
                return user;
            }
            if (error) {
                console.log(error);
                return user;
            }
            setLoading(true);
            return authService.exchangeCodeForTokens(code);
        },
        onSuccess: (user) => {
            setUser(user);
            setLoading(false);
            navigate('/dashboard');
        },
        onError: (error: any) => {
            setError(error.message || 'Authentication failed');
            setLoading(false);
        }
    });

    const logoutMutation = useMutation({
        mutationFn: async () => {
            logout();
            queryClient.clear();
        },
        onSuccess: () => {
            navigate('/');
        }
    });

    const startDiscordAuth = () => {
        window.location.href = authService.getDiscordAuthUrl();
    };

    return {
        user,
        isAuthenticated,
        isLoading: loginMutation.isPending,
        error: loginMutation.error?.message,
        login: loginMutation.mutate,
        logout: logoutMutation.mutate,
        startDiscordAuth
    };
};
